import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SettingsProvider } from "@/lib/useSettings";
import { AuthProvider } from "@/lib/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

// Corporate pages
import CorporateHome from "@/pages/CorporateHome";
import { Capabilities, Capability } from "@/pages/Capabilities";
import ServiceDetail from "@/pages/ServiceDetail";
import { Labs, LabsDetail } from "@/pages/Labs";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Partners from "@/pages/Partners";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

// Auth pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";

// Radar pages (kept intact, mounted under /radar/*)
import Home from "@/pages/Home";                     // Now the Radar landing at /radar
import News from "@/pages/News";
import Jobs from "@/pages/Jobs";
import Alerts from "@/pages/Alerts";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Search from "@/pages/Search";
import { NewsDetail, JobDetail, AlertDetail } from "@/pages/Details";
import { Tutorials, TutorialDetail } from "@/pages/Tutorials";
import SubmitNews from "@/pages/SubmitNews";
import Topic from "@/pages/Topic";
import Tech from "@/pages/Tech";
import { EcosystemHub, EcosystemList, EcosystemDetail } from "@/pages/Ecosystem";

// Admin bundle is lazy-loaded (WYSIWYG + LLM assistant off the public bundle).
const Admin = lazy(() => import("@/pages/Admin"));

import Analytics from "@/components/Analytics";

const AdminFallback = () => (
  <div className="dl-container py-20 text-[#4B5563] text-sm">Loading admin…</div>
);

// Preserve SEO for the legacy Radar-at-root URLs by 301-redirecting them
// to their new /radar/* home. Preserves the trailing id/slug segments and
// query string so social shares / GSC continue to resolve.
const RedirectToRadar = ({ suffix }) => {
  const params = useParams();
  const [sp] = useSearchParams();
  const tail = params.id ? `/${params.id}` : (params.slug ? `/${params.slug}` : (params.kindPath ? `/${params.kindPath}${params.slug ? `/${params.slug}` : ""}` : ""));
  const qs = sp.toString();
  const target = `/radar/${suffix}${tail}${qs ? `?${qs}` : ""}`;
  return <Navigate to={target} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <SettingsProvider>
          <AuthProvider>
            <BrowserRouter>
              <Analytics />
              <div className="App min-h-screen flex flex-col">
                <Header />
                <main className="flex-1">
                  <Routes>
                    {/* ---------- Corporate (www) ---------- */}
                    <Route path="/" element={<CorporateHome />} />
                    <Route path="/capabilities" element={<Capabilities />} />
                    <Route path="/capabilities/:slug" element={<Capability />} />
                    <Route path="/capabilities/:slug/:service" element={<ServiceDetail />} />
                    <Route path="/labs" element={<Labs />} />
                    <Route path="/labs/:slug" element={<LabsDetail />} />
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/clients/:slug" element={<ClientDetail />} />
                    <Route path="/partners" element={<Partners />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />

                    {/* ---------- Auth (email + password) ---------- */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

                    {/* ---------- Radar (moved under /radar/*) ---------- */}
                    <Route path="/radar" element={<Home />} />
                    <Route path="/radar/news" element={<News />} />
                    <Route path="/radar/news/:id" element={<NewsDetail />} />
                    <Route path="/radar/jobs" element={<Jobs />} />
                    <Route path="/radar/jobs/:id" element={<JobDetail />} />
                    <Route path="/radar/alerts" element={<Alerts />} />
                    <Route path="/radar/alerts/:id" element={<AlertDetail />} />
                    <Route path="/radar/insights" element={<Insights />} />
                    <Route path="/radar/insights/:id" element={<InsightDetail />} />
                    <Route path="/radar/tutorials" element={<Tutorials />} />
                    <Route path="/radar/tutorials/:id" element={<TutorialDetail />} />
                    <Route path="/radar/search" element={<Search />} />
                    <Route path="/radar/submit" element={<SubmitNews />} />
                    <Route path="/radar/topic/:slug" element={<Topic />} />
                    <Route path="/radar/tech/:slug" element={<Tech />} />
                    <Route path="/radar/ecosystem" element={<EcosystemHub />} />
                    <Route path="/radar/ecosystem/:kindPath" element={<EcosystemList />} />
                    <Route path="/radar/ecosystem/:kindPath/:slug" element={<EcosystemDetail />} />

                    {/* ---------- Legacy Radar URLs → 301 to /radar/* ---------- */}
                    {/* Preserve every externally-shared Radar URL. Backend also
                        emits Cache-Control-friendly redirects in Express SSR. */}
                    <Route path="/news" element={<RedirectToRadar suffix="news" />} />
                    <Route path="/news/:id" element={<RedirectToRadar suffix="news" />} />
                    <Route path="/jobs" element={<RedirectToRadar suffix="jobs" />} />
                    <Route path="/jobs/:id" element={<RedirectToRadar suffix="jobs" />} />
                    <Route path="/alerts" element={<RedirectToRadar suffix="alerts" />} />
                    <Route path="/alerts/:id" element={<RedirectToRadar suffix="alerts" />} />
                    <Route path="/insights" element={<RedirectToRadar suffix="insights" />} />
                    <Route path="/insights/:id" element={<RedirectToRadar suffix="insights" />} />
                    <Route path="/tutorials" element={<RedirectToRadar suffix="tutorials" />} />
                    <Route path="/tutorials/:id" element={<RedirectToRadar suffix="tutorials" />} />
                    <Route path="/search" element={<RedirectToRadar suffix="search" />} />
                    <Route path="/submit" element={<RedirectToRadar suffix="submit" />} />
                    <Route path="/topic/:slug" element={<RedirectToRadar suffix="topic" />} />
                    <Route path="/tech/:slug" element={<RedirectToRadar suffix="tech" />} />
                    <Route path="/ecosystem" element={<Navigate to="/radar/ecosystem" replace />} />
                    <Route path="/ecosystem/:kindPath" element={<RedirectToRadar suffix="ecosystem" />} />
                    <Route path="/ecosystem/:kindPath/:slug" element={<RedirectToRadar suffix="ecosystem" />} />

                    {/* Admin (unchanged path). */}
                    <Route
                      path="/admin"
                      element={(
                        <Suspense fallback={<AdminFallback />}>
                          <Admin />
                        </Suspense>
                      )}
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </BrowserRouter>
          </AuthProvider>
        </SettingsProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
